import { isNil } from 'ramda'

import {
  ReportAggregationSortType,
  ReportFilter,
  ReportFilterOperation,
  ReportMetric,
  ReportMetricType,
  ReportOrder,
  ReportOrderType,
  ReportValue,
} from '@driftt/api-types'

export const filterCreator =
  (
    property: string,
    operation: ReportFilterOperation = ReportFilterOperation.EQ
  ) =>
  (value: ReportValue[] | ReportValue): ReportFilter => {
    const filter = {
      property,
      operation,
    }

    if (!isNil(value)) {
      const valueKey =
        operation === ReportFilterOperation.IN ? 'values' : 'value'
      const newValue =
        operation === ReportFilterOperation.IN && !Array.isArray(value)
          ? [value]
          : value
      filter[valueKey] = newValue
    }

    return filter
  }

export const createOrder = (
  order: ReportOrderType = ReportOrderType.DESC
): ReportOrder => ({ order })

export const createFilter = (
  property: string,
  operation: ReportFilterOperation,
  values: ReportValue | ReportValue[] | null = null
): ReportFilter => {
  const filter: ReportFilter = {
    property,
    operation,
  }

  if (values) {
    if (Array.isArray(values)) {
      filter.values = values
    } else {
      filter.value = values
    }
  }

  return filter
}

export const createMetric = (
  property: string,
  type?: ReportMetricType | ReportAggregationSortType,
  metricType?: ReportMetricType,
  options: any = {}
): ReportMetric => {
  const metric: ReportMetric = {}
  if (property) {
    metric.property = property
  }
  if (type) {
    metric.type = type
  }
  if (metricType) {
    metric.metricType = metricType
  }

  return {
    ...metric,
    ...options,
  }
}

const SORT_BY_METRIC_OPTIONS = Object.freeze({
  conversationCount: createMetric(
    'firstAgentId',
    ReportAggregationSortType.COUNT,
    ReportMetricType.DOC_COUNT
  ),
  responseTime: createMetric(
    'timeToAgentResponseAfterBotMillis',
    ReportAggregationSortType.METRIC,
    ReportMetricType.MEDIAN
  ),
  avgResponseTime: createMetric(
    'agentAverageResponseMillis',
    ReportAggregationSortType.METRIC,
    ReportMetricType.AVG
  ),
  conversationLength: createMetric(
    'timeToCloseMillis',
    ReportAggregationSortType.METRIC,
    ReportMetricType.MEDIAN
  ),
  createdAt: createMetric('createdAt'),
  createdAtDate: createMetric('createdAtDate'),
  meetingStartAt: createMetric('meetingStartAt'),
  jobTitle: createMetric('endUser.attributes.employment_title'),
  companyName: createMetric('endUser.attributes.employment_name'),
  agentName: createMetric('agentName'),
  endUserName: [createMetric('endUser.name'), createMetric('endUser.email')],
  endUserPhone: createMetric('endUserPhone'),
  callStart: createMetric('callStart'),
  duration: createMetric('durationMillis'),
  meetingSource: createMetric('meetingSource'),
  status: createMetric('status'),
  company: createMetric('endUser.attributes.employment_name'),
  title: createMetric('endUser.attributes.employment_title'),
  playbookImpression: createMetric(
    'impressions',
    ReportAggregationSortType.METRIC,
    ReportMetricType.SUM
  ),
  playbookSent: createMetric(
    'sends',
    ReportAggregationSortType.METRIC,
    ReportMetricType.SUM
  ),
  playbookSentLegacy: createMetric(
    'sent.createdAt',
    ReportAggregationSortType.METRIC,
    ReportMetricType.COUNT
  ),
  playbookClick: createMetric(
    'click.createdAt',
    ReportAggregationSortType.METRIC,
    ReportMetricType.COUNT
  ),
  playbookConversation: createMetric(
    'conversation.createdAt',
    ReportAggregationSortType.METRIC,
    ReportMetricType.COUNT
  ),
  playbookLeadCapture: createMetric(
    'leadCapture.createdAt',
    ReportAggregationSortType.METRIC,
    ReportMetricType.COUNT
  ),
  playbookMeetingBooked: createMetric(
    'meetingBooked.createdAt',
    ReportAggregationSortType.METRIC,
    ReportMetricType.COUNT
  ),
  playbookUpdatedAt: createMetric(
    'updatedAt',
    ReportAggregationSortType.METRIC,
    ReportMetricType.COUNT
  ),
  totalMissedConversations: createMetric(
    'totalMissed',
    ReportAggregationSortType.METRIC,
    ReportMetricType.SUM
  ),
  csat: createMetric(
    'latestLiveChatRating.ratingValue',
    ReportAggregationSortType.METRIC,
    ReportMetricType.AVG
  ),
  convoCount: createMetric(
    'latestLiveChatRating.attributedRatingAgentId',
    ReportAggregationSortType.COUNT,
    ReportMetricType.DOC_COUNT
  ),
  lastRated: createMetric(
    'latestLiveChatRating.ratedAt',
    ReportAggregationSortType.METRIC,
    ReportMetricType.MAX
  ),
})

export const createSort = (
  metric: string,
  order: ReportOrderType = ReportOrderType.DESC
) => {
  const sortByOptions = SORT_BY_METRIC_OPTIONS[metric]

  if (!sortByOptions) {
    throw new ReferenceError(
      `conversation query build failed, no sort by data for ${metric}`
    )
  }

  if (Array.isArray(sortByOptions)) {
    return sortByOptions.map((metricItem) => ({
      ...metricItem,
      order,
    }))
  }
  return {
    ...sortByOptions,
    order,
  }
}
